import { SelectOptionType } from 'components/atoms/Select/Select'
import {
  CANDY_HIGHLIGHT_INDEXES,
  FUNCTIONS_TEMPLATES,
  getCandyFunctionTemplateOptions,
  isCandyMultiWashV2AllowedForCountry
} from 'types/lib/functionTemplates'
import { TEMPLATE_NAME } from 'types/lib/templates'

// TEMPLATE DISABLE FLAG: Set to false to disable "Candy" template (keep only "Candy 2")
const ENABLE_CANDY_TEMPLATE = false

const brandTemplates = [
  { name: 'Candy', id: 'Candy' },
  { name: 'Haier', id: 'Haier' },
  { name: 'HaierTV', id: 'HaierTV' },
  { name: 'Hoover', id: 'Hoover' },
  { name: 'Lenovo', id: 'Lenovo' }
]

const candyTemplates = [
  ...(ENABLE_CANDY_TEMPLATE ? [{ name: 'Candy v1', id: 'Candy' }] : []),
  { name: 'Candy', id: 'Candy 2' },
  { name: 'Candy MultiWash', id: 'Candy 3' },
  // System id Candy 4; user-facing label Candy 2 (same pattern as Candy → Candy 2)
  { name: 'Candy 2', id: TEMPLATE_NAME.CANDY4 }
]

const haierTemplates = [
  { name: 'Haier', id: 'Haier' },
  { name: 'HaierTV', id: 'HaierTV' },
  { name: 'Haier 2', id: 'Haier2' }
]

export const getTemplateOptions = (brandName?: string): SelectOptionType[] => {
  if (brandName === TEMPLATE_NAME.CANDY) {
    return candyTemplates.map(el => ({ label: el.name, value: el.id }))
  } else if (brandName === TEMPLATE_NAME.HAIER) {
    return haierTemplates.map(el => ({ label: el.name, value: el.id }))
  } else if (brandName === TEMPLATE_NAME.TEST_BRAND || brandName === TEMPLATE_NAME.DEMO) {
    return []
  } else {
    return brandTemplates.map(el => ({ label: el.name, value: el.id }))
  }
}

export const isTemplateDisabled = (brandName?: string): boolean => {
  return !(
    brandName === TEMPLATE_NAME.CANDY || brandName === TEMPLATE_NAME.HAIER
  )
}

/** Category names like "Washing machines" / "Washing machine" */
export const isWashingMachinesCategory = (categoryName?: string): boolean => {
  if (!categoryName) return false
  const n = categoryName.toLowerCase().replace(/\s+/g, ' ').trim()

  return n.includes('washing machine') || (n.includes('washing') && n.includes('machine'))
}

const isMultiwashProductName = (productName?: string): boolean =>
  !!productName && productName.toLowerCase().includes('multiwash')

export const getDefaultTemplate = (
  brandName?: string,
  categoryName?: string,
  productName?: string
): { name: string; id: string } => {
  if (brandName === TEMPLATE_NAME.HAIER) {
    // For Haier brand, check if category is TV to use HaierTV template
    if (categoryName && categoryName.toLowerCase() === 'tv') {
      return { name: 'HaierTV', id: 'HaierTV' }
    }

    return { name: 'Haier', id: 'Haier' }
  } else if (brandName === TEMPLATE_NAME.CANDY) {
    if (
      isWashingMachinesCategory(categoryName) &&
      isMultiwashProductName(productName)
    ) {
      return { name: 'Candy MultiWash', id: TEMPLATE_NAME.CANDY3 }
    }

    // If Candy template is disabled, default to Candy 2
    return { name: 'Candy 2', id: 'Candy 2' }
  } else {
    return { name: brandName || '', id: brandName || '' }
  }
}

/**
 * Default template when opening the modal with zero or more selected products.
 * For Candy + washing machines + product name containing "Multiwash" → Candy 3.
 * Multi-select: Candy 3 only if every selected product matches that rule.
 */
export const getDefaultTemplateForSelectedProducts = (
  brandName?: string,
  products?: Array<{ name: string; productCategory?: { name: string } }>
): { name: string; id: string } => {
  if (!products?.length) {
    return getDefaultTemplate(brandName)
  }

  if (products.length === 1) {
    return getDefaultTemplate(
      brandName,
      products[0].productCategory?.name,
      products[0].name
    )
  }

  if (
    brandName === TEMPLATE_NAME.CANDY &&
    products.every(
      p =>
        isWashingMachinesCategory(p.productCategory?.name) &&
        isMultiwashProductName(p.name)
    )
  ) {
    return { name: 'Candy MultiWash', id: TEMPLATE_NAME.CANDY3 }
  }

  return getDefaultTemplate(brandName, products[0]?.productCategory?.name)
}

/**
 * Normalizes template name to ensure "Candy 2" is used for Candy brand instead of "Candy"
 * @param templateName - The template name to normalize
 * @param brandName - The brand name to check against
 * @returns The normalized template name
 */
export interface TemplateResolveContext {
  categoryName?: string
  templateId?: string
  productName?: string
  country?: string
}

/** API `cardTemplate` when set; otherwise brand-specific heuristic (UI + highlights). */
export const resolveCardTemplate = (
  stored: string | null | undefined,
  brandName?: string,
  context: TemplateResolveContext = {}
): string => {
  if (stored) return stored
  if (brandName === TEMPLATE_NAME.HAIER) {
    return (
      context.templateId ||
      getDefaultTemplate(
        brandName,
        context.categoryName,
        context.productName
      ).id
    )
  }

  if (brandName === TEMPLATE_NAME.CANDY) {
    const options = getCandyFunctionTemplateOptions(context.country)

    return options[0]?.value ?? FUNCTIONS_TEMPLATES.CANDY
  }

  return ''
}

/** API `hotpointTemplate` when set; otherwise Haier heuristic (defaults to card-style pick). */
export const resolveHotpointTemplate = (
  stored: string | null | undefined,
  brandName?: string,
  context: TemplateResolveContext = {}
): string => {
  if (stored) return stored
  if (brandName === TEMPLATE_NAME.HAIER) {
    return (
      context.templateId ||
      getDefaultTemplate(
        brandName,
        context.categoryName,
        context.productName
      ).id
    )
  }

  return ''
}

export const isHaier2HotpointTemplate = (
  hotpointTemplate: string | null | undefined
): boolean => hotpointTemplate === TEMPLATE_NAME.HAIER2

export const isHaier2CardTemplate = (
  cardTemplate: string | null | undefined
): boolean => cardTemplate === TEMPLATE_NAME.HAIER2

export const isCandy4CardTemplate = (
  cardTemplate: string | null | undefined
): boolean => cardTemplate === TEMPLATE_NAME.CANDY4

export const isHaierTvCardTemplate = (
  cardTemplate: string | null | undefined
): boolean => cardTemplate === TEMPLATE_NAME.HAIER_TV

/** Minimum asset picker slots required by a card template layout (0-based indices + 1). */
const CARD_TEMPLATE_ASSET_SLOT_MINIMUMS: Record<
  string,
  Partial<Record<string, number>>
> = {
  [TEMPLATE_NAME.HAIER_TV]: { functions: 33 },
  [TEMPLATE_NAME.HAIER2]: { functions: 16 },
  [TEMPLATE_NAME.HAIER]: { functions: 16 }
}

export const getMinAssetSlotCountForCardTemplate = (
  formFieldKey: string,
  cardTemplate?: string | null
): number | undefined => {
  if (!cardTemplate) return undefined

  return CARD_TEMPLATE_ASSET_SLOT_MINIMUMS[cardTemplate]?.[formFieldKey]
}

export const getHotpointTemplateOptions = (
  brandName?: string
): SelectOptionType[] => {
  if (brandName === TEMPLATE_NAME.HAIER) {
    return getTemplateOptions(TEMPLATE_NAME.HAIER)
  }

  return []
}

const CANDY_FUNCTION_TEMPLATE_VALUES = new Set<string>(
  Object.values(FUNCTIONS_TEMPLATES)
)

/** Card visual template ids → functions layout template (when `cardTemplate` is not a function template). */
const CANDY_CARD_TO_FUNCTION_TEMPLATE: Record<string, string> = {
  [TEMPLATE_NAME.CANDY_SQUARE]: FUNCTIONS_TEMPLATES.CANDY,
  [TEMPLATE_NAME.CANDY3]: FUNCTIONS_TEMPLATES.CANDY3,
  [TEMPLATE_NAME.CANDY4]: FUNCTIONS_TEMPLATES.CANDY
}

export const isKnownCandyFunctionTemplate = (
  value: string | null | undefined
): value is keyof typeof CANDY_HIGHLIGHT_INDEXES & string =>
  !!value && CANDY_FUNCTION_TEMPLATE_VALUES.has(value)

/**
 * Resolves a Candy functions-layout template for highlights / the function-template select.
 * API `cardTemplate` may store a card visual id (e.g. "Candy 4") instead of a function template.
 */
export const resolveCandyFunctionTemplateValue = (
  stored: string | null | undefined,
  context: TemplateResolveContext = {}
): string => {
  const normalized = normalizeCandyCardTemplateForCountry(
    stored,
    context.country
  )

  if (isKnownCandyFunctionTemplate(normalized)) {
    return normalized
  }

  if (normalized && CANDY_CARD_TO_FUNCTION_TEMPLATE[normalized]) {
    return CANDY_CARD_TO_FUNCTION_TEMPLATE[normalized]
  }

  const options = getCandyFunctionTemplateOptions(context.country)

  if (
    isWashingMachinesCategory(context.categoryName) &&
    isMultiwashProductName(context.productName)
  ) {
    const multiwash = options.find(
      option => option.value === FUNCTIONS_TEMPLATES.CANDY3
    )

    if (multiwash) return multiwash.value
  }

  return options[0]?.value ?? FUNCTIONS_TEMPLATES.CANDY
}

/** When country disallows Candy MultiWash V2, downgrade stored/API template for the form. */
export const normalizeCandyCardTemplateForCountry = (
  cardTemplate: string | null | undefined,
  country?: string
): string | null | undefined => {
  if (
    cardTemplate === FUNCTIONS_TEMPLATES.CANDY3_CENEO &&
    !isCandyMultiWashV2AllowedForCountry(country)
  ) {
    return FUNCTIONS_TEMPLATES.CANDY3
  }

  return cardTemplate
}

export const normalizeTemplateName = (
  templateName: string,
  brandName?: string
): string => {
  // Enforce "Candy 2" for Candy brand - never allow "Candy" template
  if (
    brandName === TEMPLATE_NAME.CANDY &&
    templateName === TEMPLATE_NAME.CANDY
  ) {
    return TEMPLATE_NAME.CANDY_SQUARE
  }

  return templateName
}

/** User-facing label for a stored template id (system id may differ, e.g. Candy 4 → Candy 2). */
export const getTemplateDisplayName = (
  templateId: string | null | undefined,
  brandName?: string
): string => {
  if (!templateId) return ''
  const options = getTemplateOptions(brandName)
  const match = options.find(option => option.value === templateId)

  if (match) return match.label
  if (templateId === TEMPLATE_NAME.CANDY4 || templateId === FUNCTIONS_TEMPLATES.CANDY4) {
    return 'Candy 2'
  }

  return templateId
}
